import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Chip, Skeleton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid, GridActionsCellItem, GridToolbar } from "@mui/x-data-grid";
import httpClient, { extractApiError } from "../api/httpClient";
import { dataGridSx, PageHeader, PagePanel, PageShell } from "../components/PageLayout";
import DisponibilitaModal from "../components/DisponibilitaModal";
import { useUI } from "../contexts/UIContext";

const dayNames = {
  MONDAY: "Lunedì",
  TUESDAY: "Martedì",
  WEDNESDAY: "Mercoledì",
  THURSDAY: "Giovedì",
  FRIDAY: "Venerdì",
  SATURDAY: "Sabato",
  SUNDAY: "Domenica",
};

export default function Disponibilita() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locali, setLocali] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAvailability, setSelectedAvailability] = useState(null);
  const [modalMode, setModalMode] = useState("create");
  const { showNotification } = useUI();

  const normalizeAvailability = useCallback((item) => ({
    id: item?.id,
    dipendente: item?.dipendente,
    giornoSettimana: item?.giornoSettimana || item?.dayOfWeek || "MONDAY",
    tipoDisponibilita: item?.tipoDisponibilita || "DISPONIBILITA",
    pranzo: Boolean(item?.pranzo),
    cena: Boolean(item?.cena),
    localeIds: (item?.localeIds || item?.locali || []).map((entry) =>
      typeof entry === "object" ? Number(entry.id) : Number(entry)
    ),
    locali: (item?.locali || []).map((locale) => ({ id: Number(locale.id), nome: locale.nome })),
  }), []);

  const loadAvailabilities = useCallback(async () => {
    setLoading(true);
    try {
      const [availResponse, empResponse, localiResponse] = await Promise.all([
        httpClient.get("/disponibilita"),
        httpClient.get("/dipendenti"),
        httpClient.get("/locali"),
      ]);
      setRows((availResponse?.data || []).map(normalizeAvailability));
      setEmployees(empResponse?.data || []);
      setLocali(localiResponse?.data || []);
    } catch (error) {
      showNotification(extractApiError(error), "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification, normalizeAvailability]);

  useEffect(() => {
    loadAvailabilities();
  }, [loadAvailabilities]);

  const handleSave = async (payload) => {
    try {
      if (modalMode === "edit" && selectedAvailability?.id) {
        await httpClient.put(`/disponibilita/${selectedAvailability.id}`, payload);
        showNotification("Disponibilità aggiornata", "success");
      } else {
        await httpClient.post("/disponibilita", payload);
        showNotification(modalMode === "duplicate" ? "Disponibilità duplicata" : "Disponibilità creata", "success");
      }
      setModalOpen(false);
      setSelectedAvailability(null);
      setModalMode("create");
      await loadAvailabilities();
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const deleteAvailability = async (id) => {
    try {
      await httpClient.delete(`/disponibilita/${id}`);
      showNotification("Disponibilità eliminata", "success");
      await loadAvailabilities();
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 90 },
      {
        field: "dipendente",
        headerName: "Dipendente",
        minWidth: 160,
        flex: 1,
        valueGetter: (params) => params?.row?.dipendente?.nome || "-",
      },
      {
        field: "giornoSettimana",
        headerName: "Giorno",
        minWidth: 140,
        flex: 0.8,
        valueGetter: (params) => dayNames[params?.row?.giornoSettimana] || params?.row?.giornoSettimana,
      },
      {
        field: "tipoDisponibilita",
        headerName: "Tipo",
        minWidth: 140,
        flex: 0.8,
        renderCell: (params) => {
          const isReperibilita = params?.value === "REPERIBILITA";
          return (
            <Chip
              label={isReperibilita ? "Reperibilità" : "Disponibilità"}
              color={isReperibilita ? "warning" : "primary"}
              size="small"
            />
          );
        },
      },
      {
        field: "pranzo",
        headerName: "Pranzo",
        minWidth: 110,
        flex: 0.6,
        renderCell: (params) => (
          <Chip label={params.value ? "SI" : "NO"} color={params.value ? "success" : "default"} size="small" />
        ),
      },
      {
        field: "cena",
        headerName: "Cena",
        minWidth: 110,
        flex: 0.6,
        renderCell: (params) => (
          <Chip label={params.value ? "SI" : "NO"} color={params.value ? "success" : "default"} size="small" />
        ),
      },
      {
        field: "locali",
        headerName: "Locali abilitati",
        minWidth: 240,
        flex: 1.2,
        valueGetter: (params) => {
          const direct = params?.row?.locali?.map((item) => item.nome) || [];
          if (direct.length) {
            return direct.join(", ");
          }

          const ids = params?.row?.localeIds || [];
          const mapped = locali
            .filter((locale) => ids.includes(Number(locale.id)))
            .map((locale) => locale.nome);
          return mapped.join(", ") || "-";
        },
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Azioni",
        width: 120,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Modifica"
            onClick={() => {
              setSelectedAvailability(params.row);
              setModalMode("edit");
              setModalOpen(true);
            }}
          />,
          <GridActionsCellItem
            icon={<ContentCopyIcon />}
            label="Duplica"
            onClick={() => {
              setSelectedAvailability(params.row);
              setModalMode("duplicate");
              setModalOpen(true);
            }}
          />,
          <GridActionsCellItem icon={<DeleteIcon />} label="Elimina" onClick={() => deleteAvailability(params.id)} />,
        ],
      },
    ],
    [deleteAvailability, locali]
  );

  return (
    <PageShell>
      <PageHeader
        title="Gestione disponibilità"
        subtitle="Disponibilità e reperibilità per giorno, servizio e locali abilitati con la stessa gerarchia visiva della pianificazione settimanale."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedAvailability(null);
              setModalMode("create");
              setModalOpen(true);
            }}
          >
            Nuova disponibilità
          </Button>
        }
      />

      <PagePanel tone="cool" sx={{ p: 1.5 }}>
        {loading ? (
          <Skeleton variant="rounded" height={520} />
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            sx={dataGridSx}
            slots={{ toolbar: GridToolbar }}
          />
        )}
      </PagePanel>

      <DisponibilitaModal
        open={modalOpen}
        mode={modalMode}
        availability={selectedAvailability}
        employees={employees}
        locali={locali}
        onClose={() => {
          setModalOpen(false);
          setSelectedAvailability(null);
          setModalMode("create");
        }}
        onSubmit={handleSave}
      />
    </PageShell>
  );
}
